import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  IconButton,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Paper,
  ListItemIcon,
  Link,
  CircularProgress,
  Tooltip,
  TablePagination,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  NavigateNext as NavigateNextIcon,
  Business as BusinessIcon,
  AddCircleOutlineOutlined,
  Edit as EditIcon,
  DescriptionOutlined,
  WhatsApp,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { tokens } from "../../../theme";
import type { IndividualContactDetails } from "../../../types/contact";
import apiService from "../../../service/Api";
import { BookingResponse } from "../../bookings/types/view-booking";
import UploadDocuments from "../../bookings/components/UploadDocuments";
import { toast } from "react-toastify";
import {
  DocumentItem,
  PaginatedDocumentResponse,
  PaginatedResponse,
  UploadedDocumentResponse,
} from "./types/view-contact";
import formatDate from "../../../utils/FormatDate";
import { RootState } from "../../../store/Index";
import { useSelector } from "react-redux";
import ContactsNotesCard from "./NotesCard";
import { User } from "../../../store/slice/AuthSlice";
import SimilarContactsCard from "./SimilarContactsCard";
import MemorialCommentsCard from "./MemorialCommentsCard";
import ContactMergeDialog from "./ContactMergeDialog";
import type { SimilarContact, MemorialComment } from "../../../types/contact";

const IndividualContactView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const [contactData, setContactData] =
    useState<IndividualContactDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upcomingBookings, setUpcomingBookings] = useState<BookingResponse[]>(
    [],
  );
  const [pastBookings, setPastBookings] = useState<BookingResponse[]>([]);
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(5);

  const [activeTab, setActiveTab] = useState("upcoming");
  const [filteredBookings, setFilteredBookings] = useState<BookingResponse[]>(
    [],
  );
  const [isBookingLoading, setIsBookingLoading] = useState(false);
  const [dialogDocumentOpen, setDialogDocumentOpen] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentDescription, setDocumentDescription] = useState("");
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [documentCategory, setDocumentCategory] = useState("");
  const [pastBookingsCount, setPastBookingsCount] = useState(0);
  const [incomingBookingsCount, setIncomingBookingsCount] = useState(0);

  // Similar contacts state
  const [similarContacts, setSimilarContacts] = useState<SimilarContact[]>([]);
  const [similarLoading, setSimilarLoading] = useState(false);

  // Memorial comments state
  const [memorialComments, setMemorialComments] = useState<MemorialComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);

  // Merge dialog state
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [secondaryContactId, setSecondaryContactId] = useState<number | null>(null);
  const [secondaryContact, setSecondaryContact] = useState<IndividualContactDetails | null>(null);
  const [secondaryLoading, setSecondaryLoading] = useState(false);
  const [merging, setMerging] = useState(false);

  const userPermissions = useSelector(
    (state: RootState) => state.auth.user?.permissions || [],
  );
  const canMerge = (userPermissions as string[]).includes("contact.delete_contact");

  const handleAddDocument = () => {
    setDocumentName("");
    setDocumentDescription("");
    setDocumentCategory("");
    setDocumentFile(null);
    setDialogDocumentOpen(true);
  };
  const user = useSelector(
    (state: RootState) => state.auth.user,
  ) as User | null;

  const handleSaveDocument = async () => {
    if (!documentFile || !documentName || !id || !documentDescription) {
      toast.error(
        "Missing document, name, or booking ID. Please provide all details.",
        {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        },
      );
      return;
    }
    try {
      const formData = new FormData();
      formData.append("contact", id);
      formData.append("name", documentName);
      formData.append("category", documentCategory);
      formData.append("description", documentDescription);
      formData.append("document", documentFile);

      const response = await apiService.post(
        "/api/contacts/contact-documents/",
        formData,
      );
      const uploadedDoc = response.data as UploadedDocumentResponse;

      setDocuments((prev) => [
        ...prev,
        {
          id: uploadedDoc.id,
          name: uploadedDoc.name,
          description: uploadedDoc.description,
          document: uploadedDoc.document,
        },
      ]);
      toast.success("Document uploaded successfully!", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
      });
      setDialogDocumentOpen(false);
      setDocumentName("");
      setDocumentFile(null);
    } catch (error) {
      console.error("Failed to upload document:", error);
      toast.error("Failed to upload document. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    }
  };

  useEffect(() => {
    const fetchBookings = async () => {
      setIsBookingLoading(true);

      try {
        const response = await apiService.get<
          PaginatedResponse<BookingResponse>
        >(`/api/bookings/booking/?contact_id=${id}`);

        const bookings = response.data.results;

        // Filter bookings by contact ID
        const matched = bookings.filter((booking: BookingResponse) => {
          return Number(booking.contact?.id) === Number(id);
        });

        // Categorize bookings into upcoming and past
        const upcoming = matched.filter(
          (booking: BookingResponse) =>
            booking.status === "confirmed" || booking.status === "enquiry",
        );
        const past = matched.filter(
          (booking: BookingResponse) =>
            booking.status === "fulfilled" || booking.status === "completed",
        );

        // Update state with filtered and categorized bookings
        setUpcomingBookings(upcoming);
        setPastBookings(past);

        // Set the initial filtered bookings to upcoming
        setFilteredBookings(upcoming);

        // Set the counts
        setIncomingBookingsCount(upcoming.length);
        setPastBookingsCount(past.length);
      } catch (err: unknown) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message || "Failed to load bookings.";
        console.error(errorMessage);
      } finally {
        setIsBookingLoading(false);
      }
    };

    if (id) fetchBookings();
  }, [id]);

  const handleTabChange = (tab: "upcoming" | "past") => {
    setActiveTab(tab);
    setPage(0); // reset page when tab changes
    if (tab === "upcoming") {
      setFilteredBookings(upcomingBookings);
    } else {
      setFilteredBookings(pastBookings);
    }
  };

  useEffect(() => {
    const fetchContactAndDocuments = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch contact
        const contactResponse = await apiService.get<IndividualContactDetails>(
          `/api/contacts/contact/${id}/`,
        );
        setContactData(contactResponse.data);
        // Keep notes in contact details only

        // Fetch documents
        const documentsResponse =
          await apiService.get<PaginatedDocumentResponse>(
            `/api/contacts/contact-documents/?contact=${id}`,
          );

        const documentsData: DocumentItem[] =
          documentsResponse.data.results || [];
        setDocuments(documentsData);
      } catch (err: unknown) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ||
          "Failed to load individual details or documents.";
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchContactAndDocuments();
  }, [id]);

  // Fetch similar contacts
  useEffect(() => {
    const fetchSimilarContacts = async () => {
      if (!id) return;
      setSimilarLoading(true);
      try {
        const response = await apiService.get<{ similar_contacts: SimilarContact[] }>(
          `/api/contacts/contact/${id}/similar-contacts/`,
        );
        setSimilarContacts(response.data.similar_contacts || []);
      } catch (err) {
        console.error("Failed to load similar contacts:", err);
      } finally {
        setSimilarLoading(false);
      }
    };
    fetchSimilarContacts();
  }, [id]);

  // Fetch memorial comments
  useEffect(() => {
    const fetchMemorialComments = async () => {
      if (!id) return;
      setCommentsLoading(true);
      try {
        const response = await apiService.get<{ comments: MemorialComment[] }>(
          `/api/contacts/contact/${id}/memorial-comments/`,
        );
        setMemorialComments(response.data.comments || []);
      } catch (err) {
        console.error("Failed to load memorial comments:", err);
      } finally {
        setCommentsLoading(false);
      }
    };
    fetchMemorialComments();
  }, [id]);

  // Merge handlers
  const handleMergeClick = async (targetContactId: number) => {
    setSecondaryContactId(targetContactId);
    setMergeDialogOpen(true);
    setSecondaryLoading(true);
    try {
      const response = await apiService.get<IndividualContactDetails>(
        `/api/contacts/contact/${targetContactId}/`,
      );
      setSecondaryContact(response.data);
    } catch {
      setSecondaryContact(null);
    } finally {
      setSecondaryLoading(false);
    }
  };

  const handleMergeConfirm = async (
    fieldSelections: Record<string, "primary" | "secondary">,
  ) => {
    if (!contactData || !secondaryContactId) return;
    setMerging(true);
    try {
      const response = await apiService.post<IndividualContactDetails>(
        `/api/contacts/contact/${id}/merge/`,
        {
          secondary_contact_id: secondaryContactId,
          field_selections: fieldSelections,
        },
      );
      setContactData(response.data);
      setMergeDialogOpen(false);
      setSecondaryContact(null);
      setSecondaryContactId(null);
      // Refresh similar contacts
      const simResponse = await apiService.get<{ similar_contacts: SimilarContact[] }>(
        `/api/contacts/contact/${id}/similar-contacts/`,
      );
      setSimilarContacts(simResponse.data.similar_contacts || []);
      toast.success("Contacts merged successfully!", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
      });
    } catch (err) {
      console.error("Failed to merge contacts:", err);
      toast.error("Failed to merge contacts. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setMerging(false);
    }
  };

  const fullName =
    `${contactData?.first_name || ""} ${contactData?.last_name || ""}`.trim();

  const message = `Hi ${fullName}, this is ${user?.first_name} ${user?.last_name} from Muzukuru Funeral Streaming`;

  const encodedMessage = encodeURIComponent(message);
  const encodedSubject = encodeURIComponent("Muzukuru Funeral Streaming");
  const encodedBody = encodeURIComponent(message);

  const phoneNumber = contactData?.phone?.replace(/\s+/g, "") || "";

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }
  const paginatedBookings = filteredBookings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!contactData) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            sx={{
              border: `2px solid ${colors.primary[100]}`,
              color: colors.primary[100],
            }}
            onClick={() => {
              navigate(`/contacts/individual/edit/${id}`);
            }}
          >
            Edit
          </Button>
        </Box>
        <Grid container spacing={2}>
          {/* Left Column */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              style={{ height: "100%" }}
            >
              <Card sx={{ display: "flex", boxShadow: 0, height: "100%" }}>
                <Box
                  sx={{
                    flex: 1,
                    borderRight: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <CardContent sx={{ textAlign: "center", p: 3 }}>
                    <Avatar
                      src={contactData.contact_image}
                      sx={{ width: 120, height: 120, mx: "auto", mb: 2 }}
                    >
                      {contactData.name?.[0] ?? ""}
                    </Avatar>

                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      {contactData.first_name || ""}{" "}
                      {contactData.last_name || ""}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {contactData.email}
                    </Typography>

                    {/* Bookings Stats */}
                    <Box sx={{ my: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Bookings
                      </Typography>

                      <Box
                        sx={{ display: "flex", justifyContent: "space-around" }}
                      >
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="h4" fontWeight="bold">
                            {pastBookingsCount}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Past
                          </Typography>
                        </Box>

                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="h4" fontWeight="bold">
                            {incomingBookingsCount}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Upcoming
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Contact Actions */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 2,
                        mt: 1,
                      }}
                    >
                      <IconButton
                        component="a"
                        href={
                          phoneNumber
                            ? `https://wa.me/${phoneNumber}?text=${encodedMessage}`
                            : undefined
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        disabled={!phoneNumber}
                        sx={{
                          color: "#25D366",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "#25D366",
                            color: "#fff",
                            transform: "translateY(-2px)",
                          },
                          "&.Mui-disabled": {
                            backgroundColor: "background.paper",
                            color: "#BDBDBD",
                            border: "1px solid #EEEEEE",
                          },
                        }}
                      >
                        <WhatsApp />
                      </IconButton>

                      <IconButton
                        component="a"
                        href={
                          contactData.email
                            ? `mailto:${contactData.email}?subject=${encodedSubject}&body=${encodedBody}`
                            : undefined
                        }
                        disabled={!contactData.email}
                        sx={{
                          color: "#D81B60",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "#D81B60",
                            color: "#fff",
                            transform: "translateY(-2px)",
                          },
                          "&.Mui-disabled": {
                            backgroundColor: "background.paper",
                            color: "#BDBDBD",
                            border: "1px solid #EEEEEE",
                          },
                        }}
                      >
                        <EmailIcon />
                      </IconButton>

                      <IconButton
                        component="a"
                        href={phoneNumber ? `tel:${phoneNumber}` : undefined}
                        disabled={!phoneNumber}
                        sx={{
                          color: "#1976D2",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "#1976D2",
                            color: "#fff",
                            transform: "translateY(-2px)",
                          },
                          "&.Mui-disabled": {
                            backgroundColor: "background.paper",
                            color: "#BDBDBD",
                            border: "1px solid #EEEEEE",
                          },
                        }}
                      >
                        <PhoneIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Box>

                {/* Right Side - Contact Details */}
                <Box sx={{ flex: 1 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: colors.primary[300] }}
                        >
                          Phone
                        </Typography>
                        <Typography variant="h6" fontWeight="medium">
                          {contactData.phone}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: colors.primary[300] }}
                        >
                          Facebook Profile
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight="medium"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {contactData.facebook_profile ? (
                            <Link
                              href={contactData.facebook_profile}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ wordBreak: "break-word" }}
                            >
                              {contactData.facebook_profile}
                            </Link>
                          ) : (
                            "-"
                          )}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: colors.primary[300] }}
                        >
                          LinkedIn Profile
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight="medium"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {contactData.linkedin_profile ? (
                            <Link
                              href={contactData.linkedin_profile}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ wordBreak: "break-word" }}
                            >
                              {contactData.linkedin_profile}
                            </Link>
                          ) : (
                            "-"
                          )}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: colors.primary[300] }}
                        >
                          Instagram Profile
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight="medium"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {contactData.instagram_profile ? (
                            <Link
                              href={contactData.instagram_profile}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ wordBreak: "break-word" }}
                            >
                              {contactData.instagram_profile}
                            </Link>
                          ) : (
                            "-"
                          )}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: colors.primary[300] }}
                        >
                          Address One
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight="medium"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {contactData.address_one}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: colors.primary[300] }}
                        >
                          Address Two
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight="medium"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {contactData.address_two}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: colors.primary[300] }}
                        >
                          Suburb
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight="medium"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {contactData.suburb}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: colors.primary[300] }}
                        >
                          City
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight="medium"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {contactData.city}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: colors.primary[300] }}
                        >
                          Country
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight="medium"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {contactData.country}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Box>
              </Card>
            </motion.div>
          </Grid>
          {/* Notes Section */}
          <ContactsNotesCard contactId={id} />
          {/* Files/Documents Section */}
          <Grid size={3}>
            <Paper
              sx={{
                p: 2,
                height: "100%",
                boxShadow: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontSize: 16, fontWeight: "bold" }}
                >
                  Documents
                </Typography>
                <IconButton
                  onClick={handleAddDocument}
                  sx={{
                    bgcolor: theme.palette.grey[100],
                    color: theme.palette.text.secondary,
                    "&:hover": {
                      bgcolor: theme.palette.grey[200],
                    },
                  }}
                >
                  <AddCircleOutlineOutlined />
                </IconButton>
              </Box>

              {documents.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: "center",
                    mt: 2,
                  }}
                >
                  No documents found
                </Typography>
              ) : (
                <List disablePadding>
                  {documents.map((doc) => (
                    <ListItem
                      key={doc.id}
                      sx={{
                        mb: 1,
                        borderRadius: "8px",
                        border: `1px solid ${theme.palette.divider}`,
                        "&:hover": {
                          bgcolor: theme.palette.action.hover,
                          cursor: "pointer",
                        },
                        p: 1.5,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <DescriptionOutlined
                          sx={{ color: theme.palette.text.secondary }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: 14,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {doc.name}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
          {/* Bookings Table */}
          {/* Bookings Table */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.6 }}
              style={{ height: "100%" }}
            >
              <Card sx={{ boxShadow: 0, height: "100%" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        variant={
                          activeTab === "upcoming" ? "contained" : "text"
                        }
                        onClick={() => handleTabChange("upcoming")}
                        size="small"
                      >
                        Upcoming Booking
                      </Button>
                      <Button
                        variant={activeTab === "past" ? "contained" : "text"}
                        onClick={() => handleTabChange("past")}
                        size="small"
                      >
                        Past Booking
                      </Button>
                    </Box>
                  </Box>

                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: colors.primary[800] }}>
                          <TableCell>Name</TableCell>
                          <TableCell>Service</TableCell>
                          <TableCell>Package</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {isBookingLoading ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              Loading...
                            </TableCell>
                          </TableRow>
                        ) : Array.isArray(filteredBookings) &&
                          filteredBookings.length > 0 ? (
                          paginatedBookings.map((booking, index) => (
                            <TableRow
                              key={booking.id}
                              sx={{
                                backgroundColor:
                                  index % 2 === 0
                                    ? "white"
                                    : colors.primary[800],
                                cursor: "pointer", // makes it obvious the row is clickable
                                "&:hover": {
                                  backgroundColor: colors.grey[500], // hover color
                                },
                              }}
                              onClick={() =>
                                navigate(`/bookings/${booking.id}`)
                              }
                            >
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Avatar
                                    src={
                                      booking.deceased?.[0]?.person_image ?? ""
                                    }
                                    sx={{ width: 32, height: 32 }}
                                  />
                                  <Typography variant="body2">
                                    {booking?.deceased?.[0]?.first_name &&
                                    booking?.deceased?.[0]?.surname
                                      ? `${booking.deceased[0].first_name} ${booking.deceased[0].surname}`
                                      : (booking?.title ?? "")}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{booking.service}</TableCell>
                              <TableCell>
                                {booking.package_item?.name}
                              </TableCell>
                              <TableCell>
                                {formatDate(
                                  booking.services?.[0]?.date_of_service,
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              No {activeTab} bookings found.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {filteredBookings.length > rowsPerPage && (
                    <TablePagination
                      component="div"
                      count={filteredBookings.length}
                      page={page}
                      onPageChange={(event, newPage) => setPage(newPage)}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[rowsPerPage]}
                    />
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Companies Section */}
          <Grid size={{ xs: 12, md: 3 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
              style={{ height: "100%" }}
            >
              <Card sx={{ boxShadow: 0, height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Companies
                  </Typography>

                  <List dense>
                    {(contactData.companies ?? []).map((company) => (
                      <ListItem key={company.id} sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar
                            src={company.logo}
                            sx={{ width: 32, height: 32 }}
                          >
                            <BusinessIcon fontSize="small" />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary={company.name} />
                        <IconButton size="small">
                          <NavigateNextIcon />
                        </IconButton>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Payments Section */}
          <Grid size={{ xs: 12, md: 3 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
              style={{ height: "100%" }}
            >
              <Card sx={{ boxShadow: 0, height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Payments
                  </Typography>

                  {/* <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Transaction
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Amount
                      </Typography>
                    </Box>

                    {(contactData.payments ?? []).map((payment) => (
                      <Box
                        key={payment.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2">
                          {payment.transaction}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          ${payment.amount}
                        </Typography>
                      </Box>
                    ))}

                    <Divider sx={{ my: 1 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="h5" fontWeight="bold">
                        Total amount
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        ${totalAmount}
                      </Typography>
                    </Box>
                  </Box> */}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          {/* Memorial Comments Card */}
          <Grid size={{ xs: 12, md: 6 }}>
            <MemorialCommentsCard
              comments={memorialComments}
              loading={commentsLoading}
              onStreamClick={(streamId) =>
                navigate(`/memory-table/view/${streamId}`)
              }
            />
          </Grid>

          {/* Similar Contacts Card */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SimilarContactsCard
              similarContacts={similarContacts}
              loading={similarLoading}
              onContactClick={(contactId) =>
                navigate(`/contacts/individual/view/${contactId}`)
              }
              onMergeClick={handleMergeClick}
              canMerge={canMerge}
            />
          </Grid>
        </Grid>
        {/* Created By Section */}

        {(contactData.created_by || contactData.last_updated_by) && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            sx={{
              width: "100%",
              padding: "16px 24px",
              marginTop: "20px",
              backgroundColor: colors.primary[900],
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              borderRadius: "8px",
            }}
          >
            {/* 👈 Created By - Left side */}
            {contactData.created_by && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="caption"
                  color={colors.neutral[100]}
                  sx={{ fontWeight: "medium" }}
                >
                  Created By
                </Typography>

                <Tooltip
                  title={contactData.created_by?.email || ""}
                  placement="top"
                >
                  <Typography
                    variant="body2"
                    color={colors.primary[400]}
                    sx={{ fontWeight: "bold" }}
                  >
                    {contactData?.created_by?.first_name}{" "}
                    {contactData?.created_by?.last_name}
                  </Typography>
                </Tooltip>
              </Box>
            )}
            {/* 👉 Updated By - Right side */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                color={colors.neutral[100]}
                sx={{ fontWeight: "medium" }}
              >
                Last Updated By
              </Typography>

              {contactData.last_updated_by &&
              typeof contactData.last_updated_by !== "string" ? (
                <Tooltip
                  title={contactData.last_updated_by?.email || ""}
                  placement="top"
                >
                  <Typography
                    variant="body2"
                    color={colors.primary[400]}
                    sx={{ fontWeight: "bold" }}
                  >
                    {contactData.last_updated_by?.first_name}{" "}
                    {contactData.last_updated_by?.last_name}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography
                  variant="body2"
                  color={colors.primary[400]}
                  sx={{ fontWeight: "bold" }}
                >
                  —
                </Typography>
              )}
            </Box>
          </Box>
        )}
        <UploadDocuments
          open={dialogDocumentOpen}
          loading={isLoading}
          setDialogDocumentOpen={setDialogDocumentOpen}
          setDocumentCategory={setDocumentCategory}
          documentCategory={documentCategory}
          documentName={documentName}
          documentDescription={documentDescription}
          setDocumentDescription={setDocumentDescription}
          setDocumentName={setDocumentName}
          setDocumentFile={setDocumentFile}
          documentFile={documentFile}
          handleSaveDocument={handleSaveDocument}
        />
        {contactData && (
          <ContactMergeDialog
            open={mergeDialogOpen}
            onClose={() => {
              setMergeDialogOpen(false);
              setSecondaryContact(null);
              setSecondaryContactId(null);
            }}
            primaryContact={contactData}
            secondaryContact={secondaryContact}
            secondaryLoading={secondaryLoading}
            onMerge={handleMergeConfirm}
            merging={merging}
          />
        )}
      </Box>
    </motion.div>
  );
};

export default IndividualContactView;
